class CleiSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.carousel = this.querySelector('[data-clei-carousel]');
    this.inViewport = true;

    this.handleClick = (event) => {
      const button = event.target.closest('[data-clei-sound]');
      if (button && this.contains(button)) this.toggleSound(button);
    };
    this.handleResize = () => this.syncVideos();
    this.handleVisibility = () => document.hidden ? this.pauseAllVideos() : this.syncVideos();
    this.handleBlockSelect = (event) => {
      const slide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!slide) return;
      const slides = this.slides();
      const index = slides.indexOf(slide);
      if (index < 0) return;
      this.carousel?.setAttribute('xo-active-index', String(index));
      this.carousel?.setProps?.({ xoActiveIndex: index });
      requestAnimationFrame(() => this.syncVideos());
    };

    this.addEventListener('click', this.handleClick);
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('visibilitychange', this.handleVisibility);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.slideObserver = new MutationObserver(() => this.syncVideos());
    if (this.carousel) {
      this.slideObserver.observe(this.carousel, {
        subtree: true,
        attributes: true,
        attributeFilter: ['xo-visible', 'xo-active'],
      });
    }

    if ('IntersectionObserver' in window) {
      this.viewportObserver = new IntersectionObserver(([entry]) => {
        this.inViewport = entry.isIntersecting;
        this.inViewport ? this.syncVideos() : this.pauseAllVideos();
      }, { threshold: 0.2 });
      this.viewportObserver.observe(this);
    }

    requestAnimationFrame(() => this.syncVideos());
  }

  disconnectedCallback() {
    this.pauseAllVideos();
    this.slideObserver?.disconnect();
    this.viewportObserver?.disconnect();
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.initialized = false;
  }

  slides() {
    return Array.from(this.querySelectorAll('[data-clei-slide]'));
  }

  activeSlide() {
    const slides = this.slides();
    return slides.find((slide) => slide.hasAttribute('xo-visible') && slide.getAttribute('xo-visible') !== 'false')
      || slides.find((slide) => slide.hasAttribute('xo-active') && slide.getAttribute('xo-active') !== 'false')
      || slides[0]
      || null;
  }

  videoForSlide(slide) {
    if (!slide) return null;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    return (mobile && slide.querySelector('.clei-slideshow-slide__video--mobile'))
      || slide.querySelector('.clei-slideshow-slide__video--desktop')
      || slide.querySelector('video');
  }

  syncVideos() {
    const slide = this.activeSlide();
    const video = this.videoForSlide(slide);

    this.querySelectorAll('video').forEach((item) => {
      if (item !== video) {
        item.pause();
        item.muted = true;
      }
    });

    this.querySelectorAll('[data-clei-sound]').forEach((button) => {
      if (button.closest('[data-clei-slide]') !== slide) {
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', 'Unmute video');
      }
    });

    if (!video || !this.inViewport || document.hidden) return;
    const soundButton = slide?.querySelector('[data-clei-sound]');
    const soundOn = soundButton?.getAttribute('aria-pressed') === 'true';
    video.muted = !soundOn;
    video.play().catch(() => {
      video.muted = true;
      soundButton?.setAttribute('aria-pressed', 'false');
      soundButton?.setAttribute('aria-label', 'Unmute video');
      video.play().catch(() => {});
    });
  }

  pauseAllVideos() {
    this.querySelectorAll('video').forEach((video) => video.pause());
  }

  toggleSound(button) {
    const slide = button.closest('[data-clei-slide]');
    if (slide !== this.activeSlide()) return;
    const video = this.videoForSlide(slide);
    if (!video) return;

    const soundOn = button.getAttribute('aria-pressed') !== 'true';
    button.setAttribute('aria-pressed', String(soundOn));
    button.setAttribute('aria-label', soundOn ? 'Mute video' : 'Unmute video');
    video.muted = !soundOn;
    video.play().catch(() => {});
  }
}

if (!customElements.get('clei-slideshow')) {
  customElements.define('clei-slideshow', CleiSlideshow);
}
